import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { Close as CloseIcon, DragIndicator as DragIndicatorIcon, Edit as EditIcon } from '@mui/icons-material';
import { Collapse, Paper, Typography, TextField, IconButton } from '@mui/material';
import { Box } from '@mui/system';
import React, { useRef, useState } from 'react';

interface NotesListProps {
  notes: string[];
  sectionName: string;
  handleNotesUpdate: (updatedNotes: string[]) => void;
}

const NotesList: React.FC<NotesListProps> = ({ notes, sectionName, handleNotesUpdate }) => {
  const editInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [noteInEditing, setNoteInEditing] = useState<null | number>(null);

  const saveEdit = (index: number, newText: string) => {
    if (newText.trim()) {
      const updated = [...notes];
      updated[index] = newText.trim();
      handleNotesUpdate(updated);
    } else {
      handleNotesUpdate(notes.filter((_, i) => i !== index));
    }

    setNoteInEditing(null);
  };

  const handleNoteDeletion = (index: number) => {
    handleNotesUpdate(notes.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      editInputRefs.current[index]?.blur();
    }
    if (e.key === 'Escape') {
      setNoteInEditing(null);
    }
  };

  const onDragEndHandle = (result: DropResult) => {
    if (!result.destination) return;

    // Create a copy of the array
    const newNotes = Array.from(notes);
    // Remove the item from source index
    const [reorderedItem] = newNotes.splice(result.source.index, 1);
    // Add item to destination index
    newNotes.splice(result.destination.index, 0, reorderedItem);

    handleNotesUpdate(newNotes);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <DragDropContext onDragEnd={onDragEndHandle}>
        <Droppable droppableId={`droppable-${sectionName}`}>
          {(provided) => (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {notes.map((note, index) => (
                <Draggable key={`${sectionName}-${index}`} draggableId={`${sectionName}-${index}`} index={index}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps} // Draggable props go here (movement logic)
                      sx={{
                        ...provided.draggableProps.style,
                        transform: snapshot.isDragging
                          ? provided.draggableProps.style?.transform
                          : 'translate(0px, 0px)',
                      }}
                    >
                      <Collapse in={true} timeout={200}>
                        <Paper
                          elevation={snapshot.isDragging ? 4 : 0}
                          sx={{
                            p: 1.5,
                            bgcolor: snapshot.isDragging ? 'grey.100' : 'grey.50',
                            '&:hover': {
                              bgcolor: 'grey.100',
                              '& .drag-handle': { opacity: 1 },
                              '& .delete-button': { opacity: 1 },
                            },
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5 }}>
                            {/* 4. DRAG HANDLE LOGIC HERE */}
                            <Box
                              className="drag-handle"
                              {...provided.dragHandleProps}
                              sx={{
                                opacity: snapshot.isDragging ? 1 : 0,
                                transition: 'opacity 0.2s',
                                cursor: 'grab',
                                display: 'flex',
                                alignSelf: 'center',
                                '&:active': { cursor: 'grabbing' },
                                p: 0.5,
                              }}
                            >
                              <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </Box>

                            {/* Bullet point */}
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.3 }}>
                              {index + 1}.
                            </Typography>

                            {/* Note content */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {index === noteInEditing ? (
                                <TextField
                                  inputRef={(el) => (editInputRefs.current[index] = el)}
                                  defaultValue={note}
                                  onKeyDown={(event) => handleKeyPress(event, index)}
                                  onBlur={(e) => saveEdit(index, e.target.value)}
                                  fullWidth
                                  multiline
                                  variant="outlined"
                                  size="small"
                                  autoFocus
                                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                                />
                              ) : (
                                <Box
                                  onClick={() => setNoteInEditing(index)}
                                  sx={{
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'background.paper', '& .edit-icon': { opacity: 1 } },
                                    transition: 'background-color 0.2s',
                                    position: 'relative',
                                  }}
                                >
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                    {note}
                                    <EditIcon
                                      className="edit-icon"
                                      sx={{
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                        ml: 1,
                                        verticalAlign: 'middle',
                                      }}
                                    />
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* Delete button */}
                            <IconButton
                              className="delete-button"
                              onClick={() => handleNoteDeletion(index)}
                              size="small"
                              sx={{
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                color: 'text.secondary',
                                '&:hover': { color: 'error.main' },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Paper>
                      </Collapse>
                    </Box>
                  )}
                </Draggable>
              ))}
              {/* 5. Placeholder fills space while dragging */}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {notes.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No meeting notes yet. Start by adding your first point above.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NotesList;
